import { onDocumentReady, requestIdleCallback } from "@theme/utilities";

/**
 * A custom element that manages the product page sticky footer.
 *
 * Handles the visibility of a sticky footer containing important actions
 * such as Add to Cart, and dynamically adjusts the position of a third-party
 * chat button to prevent overlap.
 *
 * @extends {HTMLElement}
 */
class ProductStickyFooterComponent extends HTMLElement {
  /**
   * The product page's primary add to cart element.
   * @type {HTMLElement | null}
   */
  #addToCartButtonElement = null;

  /**
   * An intersection observer for monitoring add to cart button visibility.
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * The third-party chat button DOM element.
   * @type {string}
   */
  #chatButtonNode = null;

  /**
   * The original bottom value from the chat button element.
   * @type {string}
   */
  #originalChatButtonBottomValue = null;

  /**
   * Indicates whether the sticky footer has been activated at least once.
   * @type {boolean}
   */
  #hasHadFirstStick = false;

  /**
   * The site footer element node for applying padding adjustments.
   * @type {HTMLElement | null}
   */
  #siteFooterNode = null;

  /**
   * Assigns the original `bottom` CSS value of the chat button to preserve its initial position.
   *
   * This method:
   * - Ensures a reference to the chat button element (`#chat-button`) is stored.
   * - Retrieves the computed `bottom` value from its styles.
   * - Stores this value in `#originalChatButtonBottomValue` for later use, defaulting to `"20px"` if not found.
   *
   * Subsequent calls do nothing if the original value has already been assigned.
   *
   * @private
   * @returns {void}
   */
  #assignOriginalChatButtonBottomValue() {
    if (!this.#chatButtonNode) {
      this.#chatButtonNode = document.querySelector("#chat-button");
    }

    if (this.#originalChatButtonBottomValue) {
      return;
    }

    if (this.#chatButtonNode) {
      const computedStyle = window.getComputedStyle(this.#chatButtonNode);
      this.#originalChatButtonBottomValue = computedStyle.bottom || "20px";
    }
  }

  /**
   * Lifts the chat button above the sticky footer by adjusting its `bottom` style.
   *
   * - Ensures the original position is stored before making adjustments.
   * - Adds the sticky footer's height to the original bottom offset.
   *
   * @private
   * @returns {void}
   */
  #liftChatButton() {
    this.#assignOriginalChatButtonBottomValue();

    if (this.#originalChatButtonBottomValue) {
      // 🎗️ TODO: Add resize listener to account for browser width changes
      const footerHeight = this.offsetHeight;
      this.#chatButtonNode.style.bottom = `${parseFloat(this.#originalChatButtonBottomValue) + footerHeight}px`;
    }
  }

  /**
   * Initializes the sticky footer logic.
   *
   * - Waits for the Gorgias chat widget to load (if applicable) and adjusts its position.
   * - Creates an IntersectionObserver to toggle the sticky footer based on the
   *   visibility of the Add to Cart button.
   * - Dynamically updates the chat button's position when the sticky footer becomes active.
   *
   * @private
   * @returns {void}
   */
  #initiate = () => {
    this.#siteFooterNode = document.querySelector(
      "footer.shopify-section-group-footer-group > .section",
    );

    // Account for if the sticky is already showing and the button is then loaded
    if (!window.GorgiasChat) {
      window.addEventListener(
        "gorgias-widget-loaded",
        () => requestIdleCallback(() => this.#liftChatButton()),
        {
          once: true,
        },
      );
    }

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const classAction = entry.isIntersecting ? "remove" : "add";

      this.classList[classAction]("product-sticky-footer--active");

      // Float the chat button above the sticky footer bar
      if (classAction === "add") {
        this.#liftChatButton();

        // Add padding to site bottom on first stick to allow for a sticky bar slot
        if (!this.#hasHadFirstStick && this.#siteFooterNode) {
          this.#hasHadFirstStick = true;
          // 🎗️ TODO: Add resize listener to account for browser width changes
          this.#siteFooterNode.style.paddingBottom = `${this.offsetHeight}px`;
        }
      } else if (this.#originalChatButtonBottomValue) {
        this.#chatButtonNode.style.bottom = this.#originalChatButtonBottomValue;
      }
    });

    this.#intersectionObserver.observe(this.#addToCartButtonElement);
  };

  /**
   * Lifecycle hook called when the custom element is inserted into the DOM.
   *
   * - Retrieves the Add to Cart button element.
   * - Waits until the DOM is ready, then initializes the sticky footer logic using
   *   `scheduler.postTask` (if supported) or `requestIdleCallback` as a fallback.
   *
   * @returns {void}
   */
  connectedCallback() {
    this.#addToCartButtonElement = document.querySelector("#product-buy-box");

    if (this.#addToCartButtonElement) {
      onDocumentReady(() => {
        if ("scheduler" in window) {
          window.scheduler.postTask(() => this.#initiate(), {
            priority: "background",
          });
        } else {
          requestIdleCallback(() => this.#initiate());
        }
      });
    }
  }

  /**
   * Lifecycle hook called when the custom element is removed from the DOM.
   *
   * - Disconnects the IntersectionObserver to prevent memory leaks.
   *
   * @returns {void}
   */
  disconnectedCallback() {
    this.#intersectionObserver?.disconnect();
  }
}

if (!customElements.get("product-sticky-footer-component")) {
  customElements.define(
    "product-sticky-footer-component",
    ProductStickyFooterComponent,
  );
}
